<template>
	<div
		v-click-outside.capture="closeMenu"
		class="relative inline-flex text-left"
		@keydown.esc.stop="closeMenu({ restoreFocus: true })"
		@focusout="handleFocusOut"
	>
		<Button
			ref="trigger"
			:type="buttonType"
			:size="size"
			:disabled="loading || disabled"
			:aria-label="$t('general.decline')"
			:aria-expanded="isOpen ? 'true' : 'false'"
			aria-haspopup="menu"
			:aria-busy="loading ? 'true' : 'false'"
			class="gap-2 shrink-0 disabled:!bg-grey-50 disabled:!text-grey-500 disabled:!border-neutral-100"
			:class="[compact ? 'px-2' : '', buttonClass]"
			data-test="decline-trigger"
			@click.stop="toggleMenu"
		>
			<span class="flex items-center min-w-0 gap-1.5">
				<IconTimes size="16" class="size-4 shrink-0" />
				<span v-if="!compact" class="whitespace-nowrap">
					{{ $t('general.decline') }}
				</span>
				<IconChevronDown
					size="16"
					class="transition-transform shrink-0"
					:class="isOpen ? 'rotate-180' : ''"
				/>
			</span>
		</Button>

		<div
			v-if="isOpen && !disabled"
			ref="menu"
			:class="menuClasses"
			role="menu"
			:aria-label="$t('application.decline.choose-email-behavior')"
			@keydown.down.prevent="focusNextOption(1)"
			@keydown.up.prevent="focusNextOption(-1)"
			@keydown.tab="closeMenu()"
		>
			<p class="w-full mb-0 text-sm font-medium truncate text-grey-1000">
				{{ $t('application.decline.choose-email-behavior') }}
			</p>

			<div
				v-if="showSelectionContext"
				class="flex w-full min-w-max items-center gap-3 rounded-lg bg-grey-50 p-3"
				data-test="decline-selection-context"
			>
				<div class="flex items-center shrink-0 isolate">
					<div
						v-for="(application, index) in visibleApplications"
						:key="application.id"
						class="relative flex size-[30px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-white ring-2 ring-white"
						:class="
							index < visibleApplications.length - 1 ||
							overflowCount > 0
								? '-mr-1'
								: ''
						"
						:style="{
							zIndex: visibleApplications.length - index + 1
						}"
					>
						<Avatar
							:image="avatarOf(application)"
							:fallback="candidateInitials(application)"
							:fallback-background-color="
								fallbackAvatarBackgroundColor(index)
							"
							size="30px"
							class="!block !size-[30px] !aspect-square rounded-full text-xs !object-cover"
						/>
					</div>
					<span
						v-if="overflowCount > 0"
						class="relative z-0 inline-flex size-[30px] shrink-0 items-center justify-center rounded-full border-2 border-white bg-grey-300 text-xs font-medium text-grey-1000"
						data-test="decline-avatar-overflow"
					>
						+{{ overflowCount }}
					</span>
				</div>
				<span
					class="shrink-0 whitespace-nowrap text-sm text-grey-1000"
					data-test="decline-selection-count"
				>
					{{
						$t(selectionCountTranslationKey, {
							count: applications.length
						})
					}}
				</span>
			</div>

			<div class="flex flex-col w-full gap-2">
				<button
					type="button"
					tabindex="-1"
					class="group flex w-full flex-col items-start justify-center gap-1 rounded-lg bg-white px-3 py-2 text-left ring-1 ring-inset ring-grey-200 transition-colors hover:bg-grey-50 active:bg-[#FEF3F2] active:ring-[#FECECA] focus:outline-none focus-visible:ring-2 focus-visible:ring-grey-950"
					role="menuitem"
					data-test="decline-without-email"
					@click.stop="select(false)"
				>
					<span
						class="flex w-full items-center gap-2 whitespace-normal text-sm font-medium text-grey-800 group-active:text-[#99241B]"
					>
						<IconEnvelopeTimes size="16" class="size-4 shrink-0" />
						{{ $t('application.decline.without-email') }}
					</span>
					<span class="text-xs text-grey-600">
						{{ $t('application.decline.no-email-sent') }}
					</span>
				</button>

				<button
					type="button"
					tabindex="-1"
					class="group flex w-full flex-col items-start justify-center gap-1 rounded-lg bg-white px-3 py-2 text-left ring-1 ring-inset ring-grey-200 transition-colors hover:bg-grey-50 active:bg-[#FEF3F2] active:ring-[#FECECA] focus:outline-none focus-visible:ring-2 focus-visible:ring-grey-950"
					role="menuitem"
					data-test="decline-with-email"
					:disabled="templatesLoading || templatesError"
					@click.stop="select(true)"
				>
					<span
						class="flex w-full items-center gap-2 whitespace-normal text-sm font-medium text-grey-800 group-active:text-[#99241B]"
					>
						<IconEnvelope size="16" class="size-4 shrink-0" />
						{{ $t('application.decline.with-email') }}
					</span>
					<span
						class="max-w-full truncate text-xs text-grey-600"
						:title="defaultTemplateName"
					>
						{{
							defaultTemplateName
								? $t('application.decline.default-template', {
										name: defaultTemplateName
								  })
								: $t('general.loading')
						}}
					</span>
				</button>
				<button
					v-if="templateCount > 1"
					type="button"
					tabindex="-1"
					role="menuitem"
					data-test="decline-choose-template"
					class="group flex w-full flex-col items-start gap-1 rounded-lg px-3 py-2 text-left ring-1 ring-inset ring-grey-200 hover:bg-grey-50 active:bg-[#FEF3F2] active:ring-[#FECECA] focus-visible:ring-2 focus-visible:ring-grey-950"
					@click.stop="chooseTemplate"
				>
					<span
						class="flex w-full items-center gap-2 whitespace-normal text-sm font-medium text-grey-800 group-active:text-[#99241B]"
					>
						<IconEnvelopeMagnifyingGlass
							size="16"
							class="size-4 shrink-0"
						/>
						{{ $t('application.decline.choose-template-action') }}
					</span>
					<span class="text-xs text-grey-600">{{
						$t('application.decline.template-count', {
							count: templateCount
						})
					}}</span>
				</button>
				<p
					v-if="templatesLoading"
					role="status"
					class="m-0 text-xs text-grey-600"
				>
					{{ $t('general.loading') }}
				</p>
				<div
					v-if="templatesError"
					role="alert"
					class="text-xs text-error"
				>
					{{ $t('application.decline.request-failed') }}
					<button
						type="button"
						class="underline"
						@click.stop="$emit('open')"
					>
						{{ $t('application.decline.retry') }}
					</button>
				</div>
				<p
					v-if="showSelectionContext && recipientCount !== null"
					role="status"
					class="m-0 text-xs text-grey-600"
				>
					{{
						$t('application.decline.recipient-count', {
							count: recipientCount
						})
					}}
				</p>
			</div>
		</div>
	</div>
</template>

<script>
import { createPopper } from '@popperjs/core'
import Avatar from '~/components/avatar'
import Button from '~/components/ui/common/Button'
import {
	IconChevronDown,
	IconEnvelope,
	IconEnvelopeMagnifyingGlass,
	IconEnvelopeTimes,
	IconTimes
} from '~/components/Icons'

export default {
	name: 'DeclineActionMenu',
	components: {
		Avatar,
		Button,
		IconChevronDown,
		IconEnvelope,
		IconEnvelopeMagnifyingGlass,
		IconEnvelopeTimes,
		IconTimes
	},
	props: {
		templateCount: { type: Number, default: 1 },
		defaultTemplateName: { type: String, default: '' },
		templatesLoading: Boolean,
		templatesError: Boolean,
		recipientCount: { type: Number, default: null },
		disabled: { type: Boolean, default: false },
		applications: {
			type: Array,
			default: () => []
		},
		showSelectionContext: {
			type: Boolean,
			default: false
		},
		compact: {
			type: Boolean,
			default: false
		},
		loading: {
			type: Boolean,
			default: false
		},
		buttonType: {
			type: String,
			default: 'danger-light'
		},
		buttonClass: {
			type: String,
			default: ''
		},
		size: {
			type: String,
			default: null,
			validator: value => !value || ['sm', 'lg'].includes(value)
		}
	},
	data() {
		return {
			isOpen: false,
			popper: null
		}
	},
	computed: {
		menuClasses() {
			return [
				'fixed z-[70] flex flex-col items-start gap-3 rounded-lg bg-white p-4 ring-1 ring-inset ring-grey-200',
				'shadow-lg',
				this.showSelectionContext
					? 'w-max min-w-[263px]'
					: 'w-80 max-w-[calc(100vw-16px)]'
			]
		},
		selectionCountTranslationKey() {
			return this.applications.length === 1
				? 'application.decline.applicant-selected'
				: 'application.decline.applicants-selected'
		},
		visibleApplications() {
			return this.applications.slice(
				0,
				this.applications.length > 3 ? 2 : 3
			)
		},
		overflowCount() {
			return this.applications.length > 3
				? this.applications.length - 2
				: 0
		},
		fallbackAvatarBackgroundColors() {
			return ['#8C5CA5', '#385ACA', '#4E9970']
		}
	},
	beforeDestroy() {
		this.destroyPopper()
	},
	methods: {
		toggleMenu() {
			if (this.loading || this.disabled) return

			if (this.isOpen) {
				this.closeMenu()
				return
			}

			this.isOpen = true
			this.$emit('open')
			this.$nextTick(() => {
				this.createPopper()
				this.focusFirstOption()
			})
		},
		closeMenu(options = {}) {
			if (!this.isOpen) return

			this.isOpen = false
			this.destroyPopper()

			if (options.restoreFocus) {
				this.$nextTick(() => {
					this.focusTrigger()
				})
			}
		},
		handleFocusOut(event) {
			if (
				!this.isOpen ||
				(event.relatedTarget && this.$el.contains(event.relatedTarget))
			) {
				return
			}

			this.closeMenu()
		},
		select(sendEmail) {
			if (this.loading || this.disabled) return
			if (sendEmail && (this.templatesLoading || this.templatesError))
				return

			this.closeMenu()
			this.focusTrigger()
			this.$emit('select', sendEmail)
		},
		chooseTemplate() {
			if (
				this.loading ||
				this.disabled ||
				this.templatesLoading ||
				this.templatesError
			)
				return
			this.closeMenu()
			this.focusTrigger()
			this.$emit('choose')
		},
		focusTrigger() {
			const trigger = this.$refs.trigger?.$el || this.$refs.trigger
			trigger?.focus()
		},
		createPopper() {
			this.destroyPopper()
			const trigger = this.$refs.trigger?.$el || this.$refs.trigger

			if (!trigger || !this.$refs.menu) return

			this.popper = createPopper(trigger, this.$refs.menu, {
				placement: 'bottom-end',
				strategy: 'fixed',
				modifiers: [
					{ name: 'offset', options: { offset: [0, 8] } },
					{
						name: 'flip',
						options: { fallbackPlacements: ['top-end'] }
					},
					{ name: 'preventOverflow', options: { padding: 8 } }
				]
			})
		},
		destroyPopper() {
			if (!this.popper) return

			this.popper.destroy()
			this.popper = null
		},
		focusFirstOption() {
			this.menuOptions()[0]?.focus()
		},
		focusNextOption(direction) {
			const options = this.menuOptions()
			if (options.length === 0) return

			const currentIndex = options.indexOf(document.activeElement)
			const nextIndex =
				(currentIndex + direction + options.length) % options.length
			options[nextIndex].focus()
		},
		menuOptions() {
			return Array.from(
				this.$refs.menu?.querySelectorAll('[role="menuitem"]') || []
			)
		},
		avatarOf(application) {
			return application?.applicant_profile?.user?.avatar || null
		},
		candidateInitials(application) {
			const user = application?.applicant_profile?.user
			if (!user) return ''

			return [user.firstname, user.lastname]
				.filter(Boolean)
				.map(name =>
					String(name)
						.trim()
						.charAt(0)
						.toUpperCase()
				)
				.join('')
		},
		fallbackAvatarBackgroundColor(index) {
			return this.fallbackAvatarBackgroundColors[
				index % this.fallbackAvatarBackgroundColors.length
			]
		}
	}
}
</script>
